
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
    Container,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    LinearProgress,
    Skeleton,
    Snackbar,
    Alert
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import Ajouter from './Ajouter';
import Modifier from './Modifier';

import {
    showmodal,
    modalModifShow,
    getList,
    getPrevendeurList,
    getMarqueList,
    current,
    ajout,
    modif,
    remove
} from './objectifSlice';

// Adapte uniquement ces imports à ton arborescence
// et à ta configuration existante.
const apiUrl = process.env.REACT_APP_API_URL;

export default function Objectifs() {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const objectifData = useSelector(
        state => state.objectifsData
    );

    // À adapter si tes données d'authentification
    // proviennent d'un autre emplacement Redux.
    const { id, apitoken } = useSelector(
        state => state.userData
    );

    const [chargement, setChargement] = useState(true);
    const [suppression, setSuppression] = useState(null);

    const [messageErreur, setMessageErreur] = useState('');

    const formatMontant = (montant) => {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(montant || 0));
    };

    // -------------------------------------------------------
    // Chargement des objectifs
    // -------------------------------------------------------

    const getObjectifs = async () => {

        try {

            setChargement(true);

            const response = await axios.post(
                `${apiUrl}objectifs/${id}/${apitoken}`
            );

            dispatch(getList(response.data));

        } catch (error) {

            console.error(error);

            if (error.response?.status === 401) {
                navigate('/');
            } else {
                setMessageErreur(
                    'Impossible de charger les objectifs.'
                );
            }

        } finally {
            setChargement(false);
        }
    };

    // -------------------------------------------------------
    // Chargement des prévendeurs et marques
    // -------------------------------------------------------

    const getReferences = async () => {

        try {

            const [prevendeurs, marques] = await Promise.all([
                axios.post(
                    `${apiUrl}prevendeurs/${id}/${apitoken}`
                ),
                axios.post(
                    `${apiUrl}marques/${id}/${apitoken}`
                )
            ]);

            dispatch(
                getPrevendeurList(prevendeurs.data)
            );

            dispatch(
                getMarqueList(marques.data)
            );

        } catch (error) {

            console.error(error);

            if (error.response?.status === 401) {
                navigate('/');
            } else {
                setMessageErreur(
                    'Impossible de charger les prévendeurs ou les marques.'
                );
            }
        }
    };

    useEffect(() => {

        if (!id || !apitoken) {
            return;
        }

        getObjectifs();
        getReferences();

    }, [id, apitoken]);

    // -------------------------------------------------------
    // Ajouter
    // -------------------------------------------------------

    const ajouterObjectif = () => {
        dispatch(showmodal(true));
    };

    // -------------------------------------------------------
    // Modifier
    // -------------------------------------------------------

    const modifierObjectif = (idobjectif) => {

        const element = objectifData.list.find(
            item => item._id === idobjectif
        );

        if (!element) {
            return;
        }

        dispatch(current(element));
        dispatch(modalModifShow(true));
    };

    // -------------------------------------------------------
    // Supprimer (soft-delete backend)
    // -------------------------------------------------------

    const supprimerObjectif = async (idobjectif) => {

        const confirmation = window.confirm(
            'Voulez-vous vraiment supprimer cet objectif ?'
        );

        if (!confirmation) {
            return;
        }

        try {

            setSuppression(idobjectif);

            const response = await axios.post(
                `${apiUrl}delete/objectif/${id}/${apitoken}/${idobjectif}`
            );

            if (response.data.status === 'ok') {

                dispatch(remove(idobjectif));

            } else {

                setMessageErreur(
                    'La suppression a échoué.'
                );
            }

        } catch (error) {

            console.error(error);

            if (error.response?.status === 401) {
                navigate('/');
            } else {
                setMessageErreur(
                    'Erreur pendant la suppression.'
                );
            }

        } finally {
            setSuppression(null);
        }
    };

    // -------------------------------------------------------
    // Interface
    // -------------------------------------------------------

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                    gap: 2,
                    flexWrap: 'wrap'
                }}
            >
                <Typography variant="h5">
                    Objectifs commerciaux
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={ajouterObjectif}
                >
                    Ajouter un objectif
                </Button>
            </Box>

            <Paper elevation={2}>

                {chargement && <LinearProgress />}

                <TableContainer>

                    <Table size="small">

                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Prévendeur
                                </TableCell>

                                <TableCell>
                                    Marque
                                </TableCell>

                                <TableCell align="right">
                                    Objectif CA (DH)
                                </TableCell>

                                <TableCell align="center">
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>

                            {chargement ? (

                                Array.from({ length: 5 }).map(
                                    (_, index) => (
                                        <TableRow key={index}>
                                            {Array.from({
                                                length: 4
                                            }).map((_, col) => (
                                                <TableCell key={col}>
                                                    <Skeleton />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    )
                                )

                            ) : objectifData.list.length === 0 ? (

                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        align="center"
                                        sx={{ py: 5 }}
                                    >
                                        Aucun objectif commercial défini.
                                    </TableCell>
                                </TableRow>

                            ) : (

                                objectifData.list.map((element) => (

                                    <TableRow
                                        key={element._id}
                                        hover
                                    >
                                        <TableCell>
                                            {element.prevendeur?.nom}
                                        </TableCell>

                                        <TableCell>
                                            {element.marque?.designation}
                                        </TableCell>

                                        <TableCell align="right">
                                            {formatMontant(
                                                element.objectif_ca
                                            )}
                                        </TableCell>

                                        <TableCell align="center">

                                            <Tooltip title="Modifier">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() =>
                                                        modifierObjectif(
                                                            element._id
                                                        )
                                                    }
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Supprimer">
                                                <span>
                                                    <IconButton
                                                        color="error"
                                                        disabled={
                                                            suppression ===
                                                            element._id
                                                        }
                                                        onClick={() =>
                                                            supprimerObjectif(
                                                                element._id
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                        </TableCell>
                                    </TableRow>
                                ))

                            )}

                        </TableBody>

                    </Table>
                </TableContainer>
            </Paper>

            {/* Dialogs */}

            <Ajouter />
            <Modifier />

            {/* Notification ajout */}

            <Snackbar
                open={objectifData.ajoutOk}
                autoHideDuration={4000}
                onClose={() => dispatch(ajout(false))}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => dispatch(ajout(false))}
                >
                    Objectif ajouté avec succès.
                </Alert>
            </Snackbar>

            {/* Notification modification */}

            <Snackbar
                open={objectifData.modifOk}
                autoHideDuration={4000}
                onClose={() => dispatch(modif(false))}
            >
                <Alert
                    severity="success"
                    variant="filled"
                    onClose={() => dispatch(modif(false))}
                >
                    Objectif modifié avec succès.
                </Alert>
            </Snackbar>

            {/* Erreurs */}

            <Snackbar
                open={Boolean(messageErreur)}
                autoHideDuration={5000}
                onClose={() => setMessageErreur('')}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    onClose={() => setMessageErreur('')}
                >
                    {messageErreur}
                </Alert>
            </Snackbar>

        </Container>
    );
}
